// Customer Portal API Client

class PortalApiClient {
  private baseUrl = '/api/portal';
  private token: string | null = null;

  constructor() {
    // Load token from localStorage
    if (typeof window !== 'undefined') {
      this.token = localStorage.getItem('portal_token');
    }
  }

  setToken(token: string | null) {
    this.token = token;
    if (token) {
      localStorage.setItem('portal_token', token);
    } else {
      localStorage.removeItem('portal_token');
    }
  }

  getToken() {
    return this.token;
  }

  private async request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    };

    if (this.token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${this.baseUrl}${path}`, {
      ...options,
      headers,
      credentials: 'include',
    });

    if (response.status === 401) {
      this.setToken(null);
      window.location.href = '/portal/login';
      throw new Error('Session expired');
    }

    const data = await response.json() as T & { error?: string };

    if (!response.ok) {
      throw new Error(data.error || 'Request failed');
    }

    return data as T;
  }

  // Authentication
  async login(email: string, password: string) {
    const result = await this.request<{
      data: {
        token: string;
        user: { id: string; email: string; name: string; role: string };
        customer: { id: string; name: string; code: string };
      };
    }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    this.setToken(result.data.token);
    return result;
  }

  async logout() {
    await this.request('/auth/logout', { method: 'POST' });
    this.setToken(null);
  }

  async getMe() {
    return this.request<{
      data: {
        user: { id: string; email: string; name: string; role: string };
        customer: { id: string; code: string; name: string };
      };
    }>('/auth/me');
  }

  // Dashboard
  async getDashboard() {
    return this.request<{
      data: {
        recentOrders: any[];
        orderStats: Record<string, number>;
        documentsAvailable: number;
      };
    }>('/dashboard');
  }

  // Orders
  async getOrders(params?: { status?: string; page?: number; pageSize?: number }) {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
    const query = searchParams.toString();
    return this.request<{
      data: any[];
      page: number;
      pageSize: number;
      total: number;
      totalPages: number;
    }>(`/orders${query ? `?${query}` : ''}`);
  }

  async getOrder(id: string) {
    return this.request<{
      data: {
        id: string;
        orderNumber: string;
        status: string;
        totalAmount: string;
        currency: string;
        orderDate: string;
        requestedDeliveryDate: string;
        shippingAddress: string;
        lines: any[];
        documents: any[];
        batches: any[];
      };
    }>(`/orders/${id}`);
  }

  // Documents
  async getDocuments(params?: { type?: string; orderId?: string; page?: number; pageSize?: number }) {
    const searchParams = new URLSearchParams();
    if (params?.type) searchParams.set('type', params.type);
    if (params?.orderId) searchParams.set('orderId', params.orderId);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
    const query = searchParams.toString();
    return this.request<{
      data: any[];
      page: number;
      pageSize: number;
      total: number;
    }>(`/documents${query ? `?${query}` : ''}`);
  }

  getDocumentDownloadUrl(id: string) {
    return `${this.baseUrl}/documents/${id}/download`;
  }

  // COA
  async getBatchCoa(batchId: string) {
    return this.request<{
      data: {
        batch: any;
        testResults: any[];
        certificate: any;
        downloadAvailable: boolean;
        portalDocumentId: string | null;
      };
    }>(`/batches/${batchId}/coa`);
  }
}

export const portalApi = new PortalApiClient();
